import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { Feedback } from '../elements/common';
import { Component } from '../elements/Component';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

interface ITimeInputProps {
    className?: string;
    disabled?: boolean;
    error?: string;
    id?: string;
    onChange: (value: string | null) => Promise<void>;
    onDoValidate?: () => Promise<void>;
    placeholder?: string;
    value: string | null;
}

interface ITimeInputState {
    value: string | null;
}

const validTimeInputChar = new RegExp(/^[0-9:;,\.]$/);

export class TimeInput extends Component<FormContextProps<ITimeInputProps>, ITimeInputState> {
    public render() {
        return (
            <FormContextConsumer {...this.props}>
                {(properties) => {
                    return (
                        <>
                            <input
                                className={
                                    'form-control' +
                                    (properties.className ? ` ${properties.className}` : '') +
                                    (properties.error ? ' form-control-danger' : '')
                                }
                                disabled={properties.disabled}
                                id={properties.id}
                                onBlur={async (e) => {
                                    const parsedValue = parseEnteredValue(e.target.value);

                                    if (parsedValue !== e.target.value) {
                                        await properties.onChange(parsedValue);
                                    }

                                    if (properties.onDoValidate) {
                                        await properties.onDoValidate();
                                    }
                                }}
                                onChange={async (e) => {
                                    await properties.onChange(e.target.value);
                                }}
                                onKeyPress={async (e) => {
                                    if (validTimeInputChar.test(e.key) === false) {
                                        e.preventDefault();
                                    }
                                }}
                                placeholder={properties.placeholder}
                                type="text"
                                value={this.props.value || ''}
                            />
                            <Feedback
                                error={properties.error}
                                key="feedback"
                            />
                        </>
                    );
                }}
            </FormContextConsumer>
        );
    }
}

export function parseEnteredValue(value: string) {
    let parsedValue = value.replace(/[,\.;]/g, ':');

    if (parsedValue.match(/^[0-9]{1,4}$/g)) {
        // Fix when entering without colon. E.g.: '8' becomes '08:00', '815' becomes '08:15';
        if (parsedValue.length < 3) {
            parsedValue = `${parsedValue.padStart(2, '0')}:00`;
        } else {
            parsedValue = `${parsedValue.padStart(4, '0').substr(0, 2)}:${parsedValue.slice(-2)}`;
        }
    } else {
        // Fix when entering single digits with colon. E.g.: '8:7' becomes '08:07';
        const regex = /^([0-9]{1,2}):([0-9]{1,2})$/g;
        const matches = regex.exec(parsedValue);
        if (matches) {
            parsedValue = `${matches[1].padStart(2, '0')}:${matches[2].padStart(2, '0')}`;
        }
    }

    return parsedValue;
}

export function isValidTime(parsedTime: string) {
    const regex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/g;

    return regex.test(parsedTime);
}

export function TimeField(props: FormContextProps<ITimeInputProps & IFieldProps>) {
    return (
        <Field {...props}>
            <TimeInput {...props} />
        </Field>
    );
}
